import React, { useState, useEffect } from "react";
import api from "../api";
import { ctaToKakao } from "../utils/cloneCta";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import AddressSearch from "./AddressSearch";

export default function InlineInquiryForm({ product, onSuccess }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    inquiry_type: t.inqForm_rental,
    message: product ? `[${product.name}] ${t.inqForm_rentalMsg}` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Sync user data when auth loads after mount
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: prev.name || user.name || "",
        company: prev.company || user.company || "",
        phone: prev.phone || user.phone || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    if (ctaToKakao(e)) return;
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSubmitting(true);
    try {
      await api.post("/api/inquiries", {
        ...form,
        inquiry_type: form.inquiry_type,
        product_id: product?.id || null,
        product_name: product?.name || "",
        product_code: product?.product_code || "",
        product_source: product?._product_type || product?.source || "",
        district: product?.district || "",
      });
      setDone(true);
      if (onSuccess) onSuccess();
    } catch {
      alert(t.inqForm_error);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="p-8 text-center">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-[18px] font-bold text-[#1d1d1f] mb-2">{t.inqForm_successTitle}</h3>
        <p className="text-[14px] text-gray-500">{t.inqForm_successDesc}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-5 space-y-4">
      {product && (
        <div className="bg-[#f5f5f7] rounded-xl p-3 flex items-center gap-3">
          {product.image_url && (
            <img src={product.image_url} alt="" className="w-12 h-12 object-contain rounded-lg bg-white" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#1d1d1f] truncate">{product.name}</p>
            {product.rental_price > 0 && (
              <p className="text-[12px] text-green-600 font-medium">{t.inqForm_monthly} {product.rental_price.toLocaleString()}{t.inqForm_won}</p>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[12px] text-gray-500 mb-1 block">{t.inqForm_name}</label>
          <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="w-full bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20" />
        </div>
        <div>
          <label className="text-[12px] text-gray-500 mb-1 block">{t.inqForm_company}</label>
          <input type="text" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="w-full bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20" />
        </div>
      </div>
      <div>
        <label className="text-[12px] text-gray-500 mb-1 block">{t.inqForm_phone}</label>
        <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required placeholder="010-0000-0000" className="w-full bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20" />
      </div>
      <div>
        <label className="text-[12px] text-gray-500 mb-1 block">{t.inqForm_email}</label>
        <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20" />
      </div>
      <div>
        <AddressSearch
          value={form.address}
          label="설치 주소"
          placeholder="설치할 주소 검색 (선택사항)"
          compact
          inputClassName="bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 border-0"
          onSelect={({ address }) => setForm(f => ({...f, address}))}
          onChange={(v) => setForm(f => ({...f, address: v}))}
        />
      </div>
      <div>
        <label className="text-[12px] text-gray-500 mb-1.5 block">{t.inqForm_inquiryType}</label>
        <div className="flex flex-wrap gap-2">
          {[t.inqForm_rental, t.inqForm_purchase, t.inqForm_tonerSupplies, t.inqForm_maintenance, t.inqForm_other].map(typ => (
            <button key={typ} type="button" onClick={() => setForm({...form, inquiry_type: typ})}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition border ${
                form.inquiry_type === typ
                  ? "bg-[#0071e3] text-white border-[#0071e3]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#0071e3] hover:text-[#0071e3]"
              }`}>
              {typ}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[12px] text-gray-500 mb-1 block">{t.inqForm_content}</label>
        <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} className="w-full bg-[#f5f5f7] rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 resize-none" />
      </div>
      <button type="submit" disabled={submitting || !form.name || !form.phone} className="w-full bg-green-600 text-white rounded-xl py-3 text-[15px] font-medium hover:bg-green-700 disabled:opacity-50 transition">
        {submitting ? t.inqForm_submitting : t.inqForm_submitBtn}
      </button>
    </form>
  );
}
